import React, { useState, useEffect } from "react";
import axios from "axios";
import { 
  Bot, 
  ExternalLink, 
  Key, 
  Sparkles, 
  AlertCircle, 
  CheckCircle2, 
  Zap, 
  RefreshCw,
  Copy,
  CreditCard
} from "lucide-react";
import { Button } from "./ui/button";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AICreditsModal = ({ isOpen, onClose, user, onSaved }) => {
  const [apiKey, setApiKey] = useState("");
  const [provider, setProvider] = useState("groq"); // pestaña de clave abierta (por defecto una gratuita)
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [savingPref, setSavingPref] = useState(false);

  if (!isOpen) return null;

  const saved = user?.ai_keys_saved || [];
  const serverAi = user?.server_ai || [];
  const preference = user?.ai_provider || "auto";
  const authHeaders = () => {
    const token = localStorage.getItem("cortoviral_token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const handlePreference = async (value) => {
    setSavingPref(true);
    setMessage(null);
    if (NAMES[value]) setProvider(value); // abre la pestaña de clave de ese motor
    try {
      await axios.post(`${API}/auth/ai-preference`, { provider: value }, { headers: authHeaders(), withCredentials: true });
      if (onSaved) await onSaved();
    } catch (err) {
      setMessage({ type: "error", text: err.response?.data?.detail || "No se pudo cambiar el motor de IA." });
    } finally {
      setSavingPref(false);
    }
  };

  const handleDeleteKey = async () => {
    setSaving(true);
    setMessage(null);
    try {
      await axios.post(`${API}/auth/save-ai-key`, { provider, api_key: "" }, { headers: authHeaders(), withCredentials: true });
      setMessage({ type: "success", text: `Clave de ${NAMES[provider]} borrada.` });
      if (onSaved) onSaved();
    } catch (err) {
      setMessage({ type: "error", text: "No se pudo borrar la clave." });
    } finally {
      setSaving(false);
    }
  };

  // Aviso si eliges un motor del que no hay ninguna clave (ni tuya ni del servidor)
  const prefMissing = !["auto", "templates"].includes(preference) && !saved.includes(preference) && !serverAi.includes(preference);

  const providersInfo = {
    groq: {
      name: "Groq",
      free: true,
      portalUrl: "https://console.groq.com/keys",
      keysUrl: "https://console.groq.com/keys",
      badge: "GRATIS · sin tarjeta",
      placeholder: "gsk_...",
      desc: "Muy rápida. Cuenta gratuita sin tarjeta, con un límite diario de peticiones (de sobra para decenas de Shorts al día)."
    },
    mistral: {
      name: "Mistral AI",
      free: true,
      portalUrl: "https://console.mistral.ai/api-keys",
      keysUrl: "https://console.mistral.ai/api-keys",
      badge: "GRATIS · plan Experiment",
      placeholder: "Clave de Mistral",
      desc: "IA europea. Plan gratuito «Experiment»: pide verificar tu teléfono y permitir que usen tus peticiones para mejorar sus modelos."
    },
    openrouter: {
      name: "OpenRouter",
      free: true,
      portalUrl: "https://openrouter.ai/keys",
      keysUrl: "https://openrouter.ai/keys",
      badge: "GRATIS · modelos :free",
      placeholder: "sk-or-v1-...",
      desc: "Da acceso a varios modelos gratuitos (unos 50 guiones al día sin pagar nada). En horas punta a veces van saturados."
    },
    gemini: {
      name: "Google Gemini",
      free: true,
      portalUrl: "https://aistudio.google.com/app/apikey",
      keysUrl: "https://aistudio.google.com/app/apikey",
      badge: "Nivel gratuito con límites",
      placeholder: "AIza...",
      desc: "Clave gratuita desde Google AI Studio con límites diarios; también tiene planes de pago."
    },
    anthropic: {
      name: "Anthropic Claude",
      free: false,
      portalUrl: "https://platform.claude.com/settings/billing",
      keysUrl: "https://platform.claude.com/settings/keys",
      badge: "De pago · la mejor calidad",
      placeholder: "sk-ant-...",
      desc: "La que mejores guiones escribe. Funciona con saldo prepago en la consola de Anthropic."
    }
  };

  const currentProvider = providersInfo[provider];

  const handleSaveCustomKey = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage(null);
    try {
      const token = localStorage.getItem("cortoviral_token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.post(
        `${API}/auth/save-ai-key`,
        { provider, api_key: apiKey.trim() },
        { headers, withCredentials: true }
      );
      setApiKey("");
      setMessage({
        type: "success",
        text: res.data?.verified
          ? `Clave de ${currentProvider.name} comprobada y guardada.`
          : `Clave de ${currentProvider.name} guardada (ahora no se ha podido comprobar; si falla, verás un aviso al crear un Short).`
      });
      if (onSaved) onSaved();
    } catch (err) {
      setMessage({ type: "error", text: err.response?.data?.detail || "Error guardando la clave de API." });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start sm:items-center justify-center bg-black/85 backdrop-blur-md p-4 overflow-y-auto">
      <div 
        data-testid="ai-credits-modal"
        className="w-full max-w-lg bg-[#12141D] border border-rose-500/40 rounded-2xl p-6 sm:p-8 shadow-2xl relative space-y-5 my-auto"
      >
        <div className="flex items-center justify-between pb-3 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-rose-500/20 text-rose-400 flex items-center justify-center">
              <Bot className="w-6 h-6" />
            </div>
            <div>
              <h3 className="text-lg font-black text-white">Motores de IA</h3>
              <p className="text-xs text-slate-400">Elige con qué IA se escriben tus guiones (hay opciones gratis)</p>
            </div>
          </div>

          <button
            onClick={onClose}
            className="text-slate-400 hover:text-white text-xs p-1"
          >
            ✕
          </button>
        </div>

        {/* Nota Recordatoria */}
        <div className="p-3 bg-amber-500/10 border border-amber-500/20 rounded-xl text-xs text-amber-300 leading-relaxed">
          <strong>Aviso de Créditos:</strong> La app y tus derechos de uso están garantizados de por vida. Cada creador es responsable del saldo de IA consumido en la generación masiva de guiones.
        </div>

        {/* Motor de IA preferido */}
        <div className="bg-[#1A1D2B] border border-white/10 rounded-xl p-4 space-y-3">
          <div className="flex items-center justify-between gap-2">
            <span className="text-xs font-bold text-white">Motor de IA para tus guiones</span>
            <span data-testid="ai-engine-current" className="text-[10px] font-mono bg-emerald-500/10 text-emerald-300 border border-emerald-500/30 px-2 py-0.5 rounded-full">
              Ahora: {user?.ai_engine || "—"}
            </span>
          </div>
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
            {[["auto", "Automático"], ["groq", "Groq"], ["mistral", "Mistral"], ["openrouter", "OpenRouter"],
              ["gemini", "Gemini"], ["anthropic", "Claude"], ["templates", "Sin IA"]].map(([value, label]) => (
              <button
                key={value}
                type="button"
                data-testid={`ai-pref-${value}`}
                disabled={savingPref}
                onClick={() => handlePreference(value)}
                className={`py-2 rounded-lg text-xs font-bold border transition-all ${
                  preference === value
                    ? "bg-rose-600 border-rose-500 text-white"
                    : "bg-black/30 border-white/10 text-slate-300 hover:text-white"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
          <p className="text-[11px] text-slate-400 leading-relaxed">
            {preference === "auto"
              ? "Usa primero tus claves y, si no tienes o fallan, las del servicio."
              : preference === "templates"
                ? "No usa IA: va alternando entre 10 plantillas por nicho (o la que encaje con tu tema). No gasta créditos."
                : `Usa primero ${NAMES[preference]}; si falla, prueba con los demás motores que tengan clave.`}
          </p>
          {prefMissing && (
            <p className="text-[11px] text-amber-300">
              No hay clave de {NAMES[preference]}: añade la tuya abajo (es gratis en Groq, Mistral y OpenRouter) o se usará otro motor.
            </p>
          )}
        </div>

        {/* Tabs de Proveedores */}
        <div className="grid grid-cols-3 sm:grid-cols-5 gap-1 p-1 bg-black/40 rounded-xl border border-white/5">
          {Object.keys(providersInfo).map((key) => (
            <button
              key={key}
              type="button"
              data-testid={`ai-provider-tab-${key}`}
              onClick={() => { setProvider(key); setMessage(null); }}
              className={`py-1.5 px-1 rounded-lg text-xs font-semibold transition-all ${
                provider === key 
                  ? "bg-rose-600 text-white shadow" 
                  : "text-slate-400 hover:text-white"
              }`}
            >
              {NAMES[key]}{(user?.ai_keys_saved || []).includes(key) ? " ✓" : ""}
            </button>
          ))}
        </div>

        {/* Tarjeta de Recarga Directa al Portal Oficial */}
        <div className="bg-[#1A1D2B] border border-white/10 rounded-xl p-4 space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold text-white">{currentProvider.name}</span>
            <span className={`text-[10px] font-mono px-2 py-0.5 rounded-full border ${currentProvider.free ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" : "bg-rose-500/20 text-rose-300 border-rose-500/30"}`}>
              {currentProvider.badge}
            </span>
          </div>
          <p className="text-xs text-slate-400 leading-relaxed">
            {currentProvider.desc}
          </p>

          <div className="flex flex-col sm:flex-row gap-2 pt-2">
            <a
              data-testid="ai-portal-recharge-btn"
              href={currentProvider.portalUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex-1 py-2.5 px-3 bg-gradient-to-r from-rose-600 to-red-600 hover:from-rose-500 hover:to-red-500 text-white font-bold rounded-lg text-xs flex items-center justify-center gap-1.5 shadow-md shadow-rose-900/30 transition-all"
            >
              <CreditCard className="w-3.5 h-3.5" />
              {currentProvider.free ? `Conseguir clave gratis en ${NAMES[provider]}` : `Recargar saldo en ${NAMES[provider]}`}
              <ExternalLink className="w-3.5 h-3.5 ml-1" />
            </a>

            <a
              href={currentProvider.keysUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="py-2.5 px-3 bg-slate-800 hover:bg-slate-700 text-slate-200 font-semibold rounded-lg text-xs flex items-center justify-center gap-1 border border-white/10 transition-all"
            >
              <Key className="w-3.5 h-3.5" />
              Ver Claves
              <ExternalLink className="w-3 h-3 ml-0.5" />
            </a>
          </div>
        </div>

        {/* Input opcional para añadir clave personal */}
        <form onSubmit={handleSaveCustomKey} className="space-y-3 pt-2 border-t border-white/5">
          <div>
            <label className="block text-xs font-semibold text-slate-300 mb-1">
              ¿Deseas usar tu propia API Key personal? (Opcional)
            </label>
            <input
              data-testid="ai-custom-key-input"
              type="password"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              placeholder={currentProvider.placeholder}
              className="w-full bg-[#1A1D2B] border border-white/10 rounded-lg px-3 py-2 text-xs font-mono text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-rose-500"
            />
          </div>

          {message && (
            <div data-testid="ai-key-message" className={`p-2.5 rounded-lg text-xs flex items-center gap-2 ${message.type === 'success' ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/30' : 'bg-red-500/10 text-red-400'}`}>
              {message.type === "success" ? <CheckCircle2 className="w-4 h-4 shrink-0" /> : <AlertCircle className="w-4 h-4 shrink-0" />}
              <span>{message.text}</span>
            </div>
          )}

          <div className="flex gap-2">
            <Button
              type="button"
              variant="secondary"
              onClick={onClose}
              className="flex-1 text-xs"
            >
              Cerrar
            </Button>
            {saved.includes(provider) && (
              <Button
                data-testid="delete-ai-key-btn"
                type="button"
                variant="secondary"
                disabled={saving}
                onClick={handleDeleteKey}
                className="flex-1 text-xs text-red-300"
              >
                Borrar mi clave
              </Button>
            )}
            <Button
              data-testid="save-ai-key-btn"
              type="submit"
              disabled={saving || !apiKey.trim()}
              className="flex-1 bg-slate-800 hover:bg-slate-700 text-white font-semibold text-xs border border-white/10"
            >
              {saving ? "Comprobando..." : saved.includes(provider) ? "Cambiar mi clave" : "Guardar mi Clave"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

const NAMES = { anthropic: "Claude", gemini: "Gemini", groq: "Groq", mistral: "Mistral", openrouter: "OpenRouter" };
